/* Added the following code for FAST.Ray using AI Agent. 27/07/2026. Code Start */
/**
 * Progress rail state for the left sidebar (YOUR PROGRESS).
 * Owns visit/lite fetch, lock map, view-as / learner preview, and live checklist sync.
 * Activity components still emit window CustomEvents; this provider applies them.
 */
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";
import {
  recordProgressVisit,
  recordProgressEvent,
  fetchProgressLocks,
  formatMissingChecklist,
  getViewAsSession,
  clearViewAsSession,
  fetchViewAsProgress,
  fetchMyProgress,
  isLearnerPreviewActive,
  clearLearnerPreview,
  isRealAdminUser,
  buildResumeNavState,
  normalizePageSlug,
} from "../progressApi";
/* Added the following code for progress.ray using AI Agent. 28/07/2026. Code Start */
import {
  isChecklistComplete,
  jumpToAccordionWithRetry,
  /* Added the following code for video.ray using AI Agent. 30/07/2026. Code Start */
  jumpToChecklistActivityWithRetry,
  requestLessonVideoAutoplay,
  /* Code end. ray using AI Agent. 30/07/2026 */
} from "../progressAccordion";
import { toNavigableProgressPath } from "../pagePathUtils";
/* Code end. ray using AI Agent. 28/07/2026 */
/* Commenting the following code for progress.ray using AI Agent. 28/07/2026. Comment Start */
// import { isChecklistComplete } from "../progressAccordion";
/* Commenting end. ray using AI Agent. 28/07/2026 */

const ProgressContext = createContext(null);

const PROGRESS_SKIP_PREFIXES = [
  "/home",
  "/login",
  "/users",
  "/roles",
  "/my-learning",
  "/learning-path",
  "/learning-progress",
  "/unlock-rules",
  "/quiz-reports",
];

const LITE_PROGRESS_PATHS = ["/home", "/my-learning", "/learning-path"];

function looksLikeMePayload(detail) {
  return (
    detail &&
    typeof detail === "object" &&
    (Array.isArray(detail.checklist) ||
      detail.chapterRef != null ||
      detail.currentPage != null ||
      Array.isArray(detail.sectionPages) ||
      detail.nextTopic != null)
  );
}

function findMenuItemByPath(items, targetPath, parentPathId = "") {
  if (!Array.isArray(items)) return null;

  for (let index = 0; index < items.length; index += 1) {
    const pathId = parentPathId ? `${parentPathId}-${index}` : `${index}`;
    const item = items[index];
    if (item.path === targetPath) {
      return { item, pathId };
    }

    const childMatch = findMenuItemByPath(item.submenuItems, targetPath, pathId);
    if (childMatch) return childMatch;
  }

  return null;
}

/**
 * @param {{ children: React.ReactNode }} props
 */
export function ProgressProvider({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [menus, setMenus] = useState([]);
  const [progress, setProgress] = useState(null);
  const [lockMap, setLockMap] = useState(null);
  const [viewAs, setViewAs] = useState(() => getViewAsSession());
  const isAdminUser = isRealAdminUser();
  const [learnerPreview, setLearnerPreview] = useState(() =>
    isLearnerPreviewActive()
  );
  /* Added the following code for route_load.ray using AI Agent. Aug 12, 2026. Code Start */
  // Learners start in loading so YOUR PROGRESS can paint with the menu tree
  // instead of waiting for the first visit/lite response.
  const [progressStatus, setProgressStatus] = useState(() => {
    const admin = isRealAdminUser();
    const preview = isLearnerPreviewActive();
    const session = getViewAsSession();
    if (admin && !session?.userId && !preview) return "idle";
    return "loading";
  });
  /* Code end. ray using AI Agent. Aug 12, 2026 */
  /* Commenting the following code for route_load.ray using AI Agent. Aug 12, 2026. Comment Start */
  // const [progressStatus, setProgressStatus] = useState("idle");
  /* Commenting end. ray using AI Agent. Aug 12, 2026 */
  const [progressError, setProgressError] = useState(null);

  const checklistCompleteRef = useRef(false);
  /* Added the following code for route_load.ray using AI Agent. Aug 12, 2026. Code Start */
  // Visit sync reads the latest nav tree without re-running (and aborting) when
  // Resside registers menus after the sidebar has already started loading.
  const menusRef = useRef(menus);
  menusRef.current = menus;
  /* Code end. ray using AI Agent. Aug 12, 2026 */
  /* Added the following code for lock_gate.ray using AI Agent. 20/08/2026. Code Start */
  // Admins (not view-as / learner preview) skip linear locks; learners wait for lockMap.
  /* Added the following code for progress_nolocks.ray using AI Agent. 14/09/2026. Code Start */
  // Platform-wide: progression locks disabled for all users (tracking still runs).
  const LEARNER_PROGRESS_LOCKS_ENABLED = false;
  const bypassesProgressLocks = useCallback(() => {
    if (!LEARNER_PROGRESS_LOCKS_ENABLED) return true;
    return (
      isRealAdminUser() &&
      !getViewAsSession()?.userId &&
      !isLearnerPreviewActive()
    );
  }, []);
  /* Code end. ray using AI Agent. 14/09/2026 */
  /* Commenting the following code for progress_nolocks.ray using AI Agent. 14/09/2026. Comment Start */
  // const bypassesProgressLocks = useCallback(() => {
  //   return (
  //     isRealAdminUser() &&
  //     !getViewAsSession()?.userId &&
  //     !isLearnerPreviewActive()
  //   );
  // }, []);
  /* Commenting end. ray using AI Agent. 14/09/2026 */

  const [locksReady, setLocksReady] = useState(() => bypassesProgressLocks());
  /* Code end. ray using AI Agent. 20/08/2026 */

  /** Resside registers the nav tree so visit sync can detect leaf routes. */
  const registerMenus = useCallback((nextMenus) => {
    setMenus(Array.isArray(nextMenus) ? nextMenus : []);
  }, []);

  const isPathLocked = useCallback(
    (path) => {
      /* Added the following code for progress_nolocks.ray using AI Agent. 14/09/2026. Code Start */
      if (!LEARNER_PROGRESS_LOCKS_ENABLED) return false;
      /* Code end. ray using AI Agent. 14/09/2026 */
      if (!path || !lockMap) return false;
      /* Added the following code for lock_gate.ray using AI Agent. 20/08/2026. Code Start */
      // Match typed URLs against lockMap using the same slug/path aliases as navigation.
      const candidates = new Set();
      const addCandidate = (value) => {
        if (!value || typeof value !== "string") return;
        const normalized = value.startsWith("/") ? value : `/${value}`;
        candidates.add(normalized);
        candidates.add(normalized.toLowerCase());
        const slug = normalized.replace(/^\/+/, "").toLowerCase();
        if (slug) candidates.add(slug);
      };
      addCandidate(path);
      addCandidate(toNavigableProgressPath(path));

      const lockedPaths = lockMap.lockedPaths || [];
      const lockedSlugs = (lockMap.lockedSlugs || []).map((s) =>
        String(s).toLowerCase()
      );
      for (const candidate of candidates) {
        if (lockedPaths.includes(candidate)) return true;
        const slug = String(candidate).replace(/^\/+/, "").toLowerCase();
        if (slug && lockedSlugs.includes(slug)) return true;
      }
      return false;
      /* Code end. ray using AI Agent. 20/08/2026 */
      /* Commenting the following code for lock_gate.ray using AI Agent. 20/08/2026. Comment Start */
      // const normalized = path.startsWith("/") ? path : `/${path}`;
      // const slug = normalized.replace(/^\/+/, "").toLowerCase();
      // return (
      //   (lockMap.lockedPaths || []).includes(normalized) ||
      //   (lockMap.lockedPaths || []).includes(normalized.toLowerCase()) ||
      //   (lockMap.lockedSlugs || []).includes(slug)
      // );
      /* Commenting end. ray using AI Agent. 20/08/2026 */
    },
    [lockMap]
  );

  const refreshLocks = useCallback(async () => {
    /* Added the following code for lock_gate.ray using AI Agent. 20/08/2026. Code Start */
    if (bypassesProgressLocks()) {
      setLocksReady(true);
      return;
    }
    /* Code end. ray using AI Agent. 20/08/2026 */
    const session = getViewAsSession();
    if (session?.userId) {
      try {
        const { status, body } = await fetchViewAsProgress(session.userId);
        if (status >= 200 && status < 300 && body?.success) {
          setLockMap(body.data?.lockMap || null);
          setProgress(body.data);
          setViewAs(body.data?.viewAs || session);
          setProgressStatus("ready");
          setProgressError(null);
          /* Added the following code for lock_gate.ray using AI Agent. 20/08/2026. Code Start */
          setLocksReady(true);
          /* Code end. ray using AI Agent. 20/08/2026 */
        }
      } catch (err) {
        console.error("view-as locks:", err);
        /* Added the following code for lock_gate.ray using AI Agent. 20/08/2026. Code Start */
        // Do not block navigation forever if the locks request fails.
        setLocksReady(true);
        /* Code end. ray using AI Agent. 20/08/2026 */
      }
      return;
    }
    try {
      const { status, body } = await fetchProgressLocks();
      if (status >= 200 && status < 300 && body?.success) {
        setLockMap(body.data);
        /* Added the following code for lock_gate.ray using AI Agent. 20/08/2026. Code Start */
        setLocksReady(true);
        /* Code end. ray using AI Agent. 20/08/2026 */
      } else {
        /* Added the following code for lock_gate.ray using AI Agent. 20/08/2026. Code Start */
        setLocksReady(true);
        /* Code end. ray using AI Agent. 20/08/2026 */
      }
    } catch (err) {
      console.error("progress locks:", err);
      /* Added the following code for lock_gate.ray using AI Agent. 20/08/2026. Code Start */
      setLocksReady(true);
      /* Code end. ray using AI Agent. 20/08/2026 */
    }
  }, [bypassesProgressLocks]);

  /**
   * Apply /me-shaped payload optimistically.
   * Full lockMap refetch only when checklist completion flips unlocked, or payload lacks lockMap after unlock.
   */
  const applyPayload = useCallback((payload, { forceLocks = false } = {}) => {
    if (!payload) return;
    setProgress(payload);
    if (payload.lockMap) setLockMap(payload.lockMap);
    /* Added the following code for lock_gate.ray using AI Agent. 20/08/2026. Code Start */
    if (payload.lockMap) setLocksReady(true);
    /* Code end. ray using AI Agent. 20/08/2026 */
    setProgressStatus("ready");
    setProgressError(null);

    const nowComplete = isChecklistComplete(payload.checklist);
    const flippedToComplete = nowComplete && !checklistCompleteRef.current;
    checklistCompleteRef.current = nowComplete;

    return { flippedToComplete, forceLocks };
  }, []);

  /* Added the following code for route_load.ray using AI Agent. Aug 12, 2026. Code Start */
  useEffect(() => {
    refreshLocks();
  }, [refreshLocks]);
  /* Code end. ray using AI Agent. Aug 12, 2026 */
  /* Commenting the following code for route_load.ray using AI Agent. Aug 12, 2026. Comment Start */
  // useEffect(() => {
  //   refreshLocks();
  // }, [refreshLocks, menus]);
  /* Commenting end. ray using AI Agent. Aug 12, 2026 */

  useEffect(() => {
    const onViewAs = () => {
      setViewAs(getViewAsSession());
      /* Added the following code for lock_gate.ray using AI Agent. 20/08/2026. Code Start */
      setLocksReady(bypassesProgressLocks());
      /* Code end. ray using AI Agent. 20/08/2026 */
      refreshLocks();
    };
    window.addEventListener("selftrainai:view-as-changed", onViewAs);
    return () => window.removeEventListener("selftrainai:view-as-changed", onViewAs);
  }, [refreshLocks, bypassesProgressLocks]);

  useEffect(() => {
    const onPreview = () => {
      setLearnerPreview(isLearnerPreviewActive());
      /* Added the following code for lock_gate.ray using AI Agent. 20/08/2026. Code Start */
      setLocksReady(bypassesProgressLocks());
      /* Code end. ray using AI Agent. 20/08/2026 */
      refreshLocks();
      window.dispatchEvent(
        new CustomEvent("selftrainai:progress-updated", {
          detail: { source: "learner-preview" },
        })
      );
    };
    window.addEventListener("selftrainai:learner-preview-changed", onPreview);
    return () =>
      window.removeEventListener("selftrainai:learner-preview-changed", onPreview);
  }, [refreshLocks, bypassesProgressLocks]);

  /* Added the following code for route_load.ray using AI Agent. Aug 12, 2026. Code Start */
  /**
   * Fill YOUR PROGRESS from GET /me?lite=1 as soon as the layout mounts.
   * Runs in parallel with nav headers; lesson visits can update the rail afterward.
   */
  useEffect(() => {
    if (isAdminUser && !getViewAsSession()?.userId && !isLearnerPreviewActive()) {
      return;
    }
    const path = location.pathname || "";
    const onLitePath = LITE_PROGRESS_PATHS.some(
      (p) => path === p || path.startsWith(`${p}/`)
    );
    if (onLitePath) return;

    let cancelled = false;
    setProgressStatus((s) => (s === "ready" ? s : "loading"));
    (async () => {
      try {
        const session = getViewAsSession();
        if (session?.userId) {
          const { status, body } = await fetchViewAsProgress(session.userId);
          if (cancelled) return;
          if (status >= 200 && status < 300 && body?.success) {
            setProgress((current) => current || body.data);
            if (body.data?.lockMap) {
              setLockMap((current) => current || body.data.lockMap);
            }
            setViewAs(body.data?.viewAs || session);
            setProgressStatus("ready");
            setProgressError(null);
          }
          return;
        }
        const { status, body } = await fetchMyProgress(undefined, { lite: true });
        if (cancelled) return;
        if (status >= 200 && status < 300 && body?.success) {
          // Visit may already have painted a page-specific payload; keep that.
          setProgress((current) => current || body.data);
          if (body.data?.lockMap) {
            setLockMap((current) => current || body.data.lockMap);
          }
          setProgressStatus("ready");
          setProgressError(null);
        }
      } catch (err) {
        console.error("eager progress:", err);
      }
    })();

    return () => {
      cancelled = true;
    };
    // Initial path only — later lesson navigations are handled by visit sync.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewAs?.userId, isAdminUser, learnerPreview, applyPayload]);
  /* Code end. ray using AI Agent. Aug 12, 2026 */

  // Hub pages: lite /me without writing a visit.
  useEffect(() => {
    const path = location.pathname || "";
    const wantsLite = LITE_PROGRESS_PATHS.some(
      (p) => path === p || path.startsWith(`${p}/`)
    );
    if (!wantsLite) return;
    if (isAdminUser && !getViewAsSession()?.userId && !isLearnerPreviewActive()) {
      return;
    }

    let cancelled = false;
    setProgressStatus((s) => (s === "ready" ? s : "loading"));
    (async () => {
      try {
        const session = getViewAsSession();
        if (session?.userId) {
          const { status, body } = await fetchViewAsProgress(session.userId);
          if (cancelled) return;
          if (status >= 200 && status < 300 && body?.success) {
            applyPayload(body.data);
            if (body.data?.lockMap) setLockMap(body.data.lockMap);
            setViewAs(body.data?.viewAs || session);
          } else {
            setProgressStatus("error");
            setProgressError("Could not load progress.");
          }
          return;
        }
        const { status, body } = await fetchMyProgress(undefined, { lite: true });
        if (cancelled) return;
        if (status >= 200 && status < 300 && body?.success) {
          applyPayload(body.data);
        } else {
          setProgressStatus("error");
          setProgressError("Could not load progress.");
        }
      } catch (err) {
        console.error("lite progress:", err);
        if (!cancelled) {
          setProgressStatus("error");
          setProgressError("Could not load progress.");
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [location.pathname, viewAs?.userId, isAdminUser, learnerPreview, applyPayload]);

  // Lesson visits + lock redirects.
  useEffect(() => {
    const path = location.pathname || "";
    if (!path || path === "/") return;
    if (PROGRESS_SKIP_PREFIXES.some((p) => path === p || path.startsWith(`${p}/`))) {
      return;
    }

    /* Added the following code for lock_gate.ray using AI Agent. 20/08/2026. Code Start */
    // Wait for lockMap before visit/mount decisions so typed URLs cannot flash unlocked content.
    if (!bypassesProgressLocks() && !locksReady) {
      return;
    }

    // Known-locked path: bounce to resume without calling recordProgressVisit.
    if (!bypassesProgressLocks() && isPathLocked(path)) {
      const rawResume =
        lockMap?.resumePath ||
        (lockMap?.resumePageSlug ? `/${lockMap.resumePageSlug}` : null) ||
        "/my-learning";
      const resumePath =
        toNavigableProgressPath(rawResume) || rawResume || "/my-learning";
      const samePage =
        normalizePageSlug(toNavigableProgressPath(path) || path) ===
        normalizePageSlug(toNavigableProgressPath(resumePath) || resumePath);
      if (!samePage && location.pathname !== resumePath) {
        const missing = formatMissingChecklist(lockMap?.missingChecklist);
        toast.error(
          lockMap?.lockMessage ||
            (missing
              ? `Locked — finish first: ${missing}`
              : "This content is locked.")
        );
        navigate(resumePath, {
          replace: true,
          state: buildResumeNavState({ lockMap }),
        });
      }
      return;
    }
    /* Code end. ray using AI Agent. 20/08/2026 */

    let cancelled = false;
    const syncProgress = async () => {
      setProgressStatus((s) => (s === "ready" ? s : "loading"));
      try {
        const session = getViewAsSession();
        if (session?.userId) {
          const { status, body } = await fetchViewAsProgress(session.userId, path);
          if (cancelled) return;
          if (status >= 200 && status < 300 && body?.success) {
            applyPayload(body.data);
            if (body.data?.lockMap) setLockMap(body.data.lockMap);
            setViewAs(body.data?.viewAs || session);
          } else {
            setProgressStatus("error");
            setProgressError("Could not load progress.");
          }
          return;
        }

        const { status, body } = await recordProgressVisit(path);
        if (cancelled) return;
        if (status === 403 && body?.locked) {
          const resumePath =
            body?.data?.resume?.path ||
            body?.data?.access?.resumePath ||
            body?.data?.lockMap?.resumePath ||
            "/my-learning";
          if (body?.data?.lockMap) setLockMap(body.data.lockMap);
          const missing =
            formatMissingChecklist(body?.data?.access?.missingChecklist) ||
            formatMissingChecklist(body?.data?.lockMap?.missingChecklist);
          const lockMsg =
            body?.message ||
            body?.data?.lockMap?.lockMessage ||
            (missing
              ? `Locked — finish first: ${missing}`
              : "This content is locked.");
          toast.error(lockMsg);
          if (location.pathname !== resumePath) {
            navigate(resumePath, {
              replace: true,
              state: buildResumeNavState({
                resume: body?.data?.resume,
                lockMap: body?.data?.lockMap,
                access: body?.data?.access,
              }),
            });
          }
          setProgressStatus("ready");
          return;
        }
        if (status >= 200 && status < 300 && body?.success) {
          applyPayload(body.data);
          if (body.data?.lockMap) setLockMap(body.data.lockMap);
          const navState = location.state || {};
          const resumeState = navState.resumeState || null;
          if (resumeState?.scrollY != null) {
            window.setTimeout(() => {
              window.scrollTo({
                top: Number(resumeState.scrollY) || 0,
                behavior: "smooth",
              });
            }, 400);
          }
          /* Commenting the following code for progress.ray using AI Agent. 28/07/2026. Comment Start */
          // Accordion resume moved to a dedicated location.key effect (avoids double-toggle
          // and covers same-path "Go to Test it" navigations that do not re-run visit).
          // const accordionId = resumeState?.accordionId || resumeState?.section || null;
          // if (accordionId) { jumpToAccordionWithRetry(...); }
          /* Commenting end. ray using AI Agent. 28/07/2026 */
          recordProgressEvent({
            type: "save_resume",
            pageSlug: path,
            scrollY: typeof window !== "undefined" ? window.scrollY : 0,
          }).catch(() => {});
        } else {
          setProgressStatus("error");
          setProgressError("Could not update progress.");
        }
      } catch (err) {
        console.error("progress visit:", err);
        if (!cancelled) {
          setProgressStatus("error");
          setProgressError("Could not update progress.");
        }
      }
    };

    const located = findMenuItemByPath(menusRef.current, path);
    /* Added the following code for menu.ray using AI Agent. 28/07/2026. Code Start */
    // Pure folders with no route: no progress call.
    if (menusRef.current.length && located && located.item?.submenu && !located.item?.path) {
      return;
    }
    // Folder hubs that still have a path: record hub visit (backend overview path —
    // enrollment + section coverage only; no leaf checklist writes).
    const isFolderWithPath =
      !!located?.item?.path &&
      (!!located.item.submenu || located.item.isLeaf === false);
    if (isFolderWithPath) {
      syncProgress();
      return () => {
        cancelled = true;
      };
    }
    /* Code end. ray using AI Agent. 28/07/2026 */

    /* Commenting the following code for menu.ray using AI Agent. 28/07/2026. Comment Start */
    // Previously folder hubs used fetchMyProgress only (no visit), so hub coverage
    // could never record. Backend overview visit is now safe + records hubs.
    // const isFolderWithPath = ...; fetchMyProgress / fetchViewAsProgress ...
    /* Commenting end. ray using AI Agent. 28/07/2026 */

    if (menusRef.current.length && !located) {
      syncProgress();
    } else if (
      !menusRef.current.length ||
      (located?.item?.path && !located.item.submenu) ||
      located?.item?.path
    ) {
      syncProgress();
    }

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, viewAs?.userId, locksReady]);

  /* Commenting the following code for menu.ray using AI Agent. 28/07/2026. Comment Start */
  // Previously any menu item with a path (including folder hubs) called recordProgressVisit,
  // which could alias parent URLs onto child leaves and inflate chapter progress.
  // const located = findMenuItemByPath(menus, path);
  // if (menus.length && located && located.item?.submenu && !located.item?.path) return;
  // if (...) syncProgress();
  /* Commenting end. ray using AI Agent. 28/07/2026 */

  /* Added the following code for progress.ray using AI Agent. 28/07/2026. Code Start */
  // Open the target accordion after Resume / Go to Test it (including same-path navigations).
  useEffect(() => {
    const resumeState = location.state?.resumeState;
    const accordionId =
      resumeState?.accordionId || resumeState?.section || null;
    if (!accordionId) return undefined;

    let cancelled = false;
    const timer = window.setTimeout(() => {
      if (cancelled) return;
      /* Added the following code for video.ray using AI Agent. 30/07/2026. Code Start */
      // Video lives in the header player, not a lesson accordion — open + muted autoplay.
      if (String(accordionId).toLowerCase() === "video") {
        const pageSlug = normalizePageSlug(location.pathname || "");
        const wantAutoplay = resumeState?.autoplayVideo === true;
        if (wantAutoplay && pageSlug) {
          /* Commenting the following code for testit.ray using AI Agent. 2026-08-07. Comment Start */
          // requestLessonVideoAutoplay(pageSlug);
          /* Commenting end. ray using AI Agent. 2026-08-07 */
        }
        jumpToChecklistActivityWithRetry(
          { id: "video", label: "Watch video" },
          {
            attempts: 15,
            delayMs: 120,
            /* Added the following code for testit.ray using AI Agent. 2026-08-07. Code Start */
            autoplay: false,
            /* Code end. ray using AI Agent. 2026-08-07 */
            /* Commenting the following code for testit.ray using AI Agent. 2026-08-07. Comment Start */
            // autoplay: wantAutoplay,
            /* Commenting end. ray using AI Agent. 2026-08-07 */
            pageSlug,
          }
        );
        return;
      }
      /* Code end. ray using AI Agent. 30/07/2026 */
      jumpToAccordionWithRetry(
        accordionId,
        resumeState?.nextChecklistItem?.label || null,
        { attempts: 15, delayMs: 120 }
      );
    }, 150);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [location.pathname, location.key]);
  /* Code end. ray using AI Agent. 28/07/2026 */

  // Live checklist sync from accordion / video / quiz / Try it.
  useEffect(() => {
    let cancelled = false;
    let locksTimer = null;
    let refreshTimer = null;

    const scheduleLocksRefresh = () => {
      if (locksTimer) window.clearTimeout(locksTimer);
      locksTimer = window.setTimeout(() => {
        refreshLocks();
      }, 600);
    };

    const maybeRefreshLocks = (meta) => {
      if (meta?.forceLocks || meta?.flippedToComplete) {
        scheduleLocksRefresh();
      }
    };

    const refreshFromServer = async (pageSlugHint) => {
      try {
        const session = getViewAsSession();
        if (session?.userId) {
          const { status, body } = await fetchViewAsProgress(
            session.userId,
            pageSlugHint || location.pathname
          );
          if (cancelled) return;
          if (status >= 200 && status < 300 && body?.success) {
            const meta = applyPayload(body.data);
            setViewAs(body.data?.viewAs || session);
            maybeRefreshLocks(meta);
          }
          return;
        }
        const slug = pageSlugHint || location.pathname || "";
        const { status, body } = await fetchMyProgress(slug, { lite: true });
        if (cancelled) return;
        if (status >= 200 && status < 300 && body?.success) {
          const meta = applyPayload(body.data);
          maybeRefreshLocks(meta);
        }
      } catch (err) {
        console.error("progress rail refresh:", err);
      }
    };

    const onProgressPayload = (event) => {
      const payload = event?.detail;
      if (looksLikeMePayload(payload)) {
        // Optimistic apply from event body — skip lock refetch unless unlock flipped.
        const meta = applyPayload(payload);
        maybeRefreshLocks(meta);
      }
    };

    const onProgressUpdated = (event) => {
      const detail = event?.detail;
      if (looksLikeMePayload(detail)) {
        const meta = applyPayload(detail);
        maybeRefreshLocks(meta);
        return;
      }
      const source = detail?.source || "";
      const delay = source === "video_progress" ? 150 : 0;
      if (refreshTimer) window.clearTimeout(refreshTimer);
      refreshTimer = window.setTimeout(() => {
        refreshFromServer(detail?.pageSlug || location.pathname);
      }, delay);
    };

    window.addEventListener("selftrainai:progress-updated", onProgressUpdated);
    window.addEventListener("selftrainai:progress-payload", onProgressPayload);
    return () => {
      cancelled = true;
      if (locksTimer) window.clearTimeout(locksTimer);
      if (refreshTimer) window.clearTimeout(refreshTimer);
      window.removeEventListener("selftrainai:progress-updated", onProgressUpdated);
      window.removeEventListener("selftrainai:progress-payload", onProgressPayload);
    };
  }, [location.pathname, refreshLocks, viewAs?.userId, applyPayload]);

  /* Added the following code for route_load.ray using AI Agent. Aug 12, 2026. Code Start */
  const isLearnerRailUser =
    !isAdminUser || !!viewAs?.userId || learnerPreview;
  const showProgressRail =
    isLearnerRailUser &&
    (progressStatus === "idle" ||
      progressStatus === "loading" ||
      progressStatus === "error" ||
      !!progress?.chapterRef);
  /* Code end. ray using AI Agent. Aug 12, 2026 */
  /* Commenting the following code for route_load.ray using AI Agent. Aug 12, 2026. Comment Start */
  // const showProgressRail =
  //   (!!progress?.chapterRef || progressStatus === "loading" || progressStatus === "error") &&
  //   (!isAdminUser || !!viewAs?.userId || learnerPreview);
  /* Commenting end. ray using AI Agent. Aug 12, 2026 */

  const onResume = useCallback(() => {
    if (!progress?.resume?.path) return;
    /* Added the following code for progress.ray using AI Agent. 28/07/2026. Code Start */
    const targetPath = toNavigableProgressPath(progress.resume.path);
    /* Code end. ray using AI Agent. 28/07/2026 */
    navigate(targetPath, {
      state: {
        pageName: progress.resume.label,
        resumeState:
          progress.resume.resumeState || lockMap?.resumeState || null,
      },
    });
  }, [progress, lockMap, navigate]);

  const retryProgress = useCallback(async () => {
    setProgressError(null);
    setProgressStatus("loading");
    const path = location.pathname || "";
    const skipVisit = PROGRESS_SKIP_PREFIXES.some(
      (p) => path === p || path.startsWith(`${p}/`)
    );
    try {
      if (skipVisit) {
        const slug =
          progress?.currentPage?.unitKey ||
          progress?.resume?.pageSlug ||
          undefined;
        const { status, body } = await fetchMyProgress(slug, { lite: true });
        if (status >= 200 && status < 300 && body?.success) {
          applyPayload(body.data);
        } else {
          setProgressStatus("error");
          setProgressError("Could not load progress.");
        }
        return;
      }
      const { status, body } = await recordProgressVisit(path);
      if (status >= 200 && status < 300 && body?.success) {
        applyPayload(body.data);
        if (body.data?.lockMap) setLockMap(body.data.lockMap);
      } else {
        setProgressStatus("error");
        setProgressError("Could not load progress.");
      }
    } catch (err) {
      console.error("progress retry:", err);
      setProgressStatus("error");
      setProgressError("Could not load progress.");
    }
  }, [location.pathname, progress, applyPayload]);

  const exitViewAs = useCallback(() => {
    clearViewAsSession();
    setViewAs(null);
    setProgress(null);
    setProgressStatus("idle");
    /* Added the following code for lock_gate.ray using AI Agent. 20/08/2026. Code Start */
    setLocksReady(bypassesProgressLocks());
    /* Code end. ray using AI Agent. 20/08/2026 */
    refreshLocks();
    toast.success("Exited view-as mode");
  }, [refreshLocks, bypassesProgressLocks]);

  const exitLearnerPreview = useCallback(() => {
    clearLearnerPreview();
    setLearnerPreview(false);
    setProgress(null);
    setProgressStatus("idle");
    /* Added the following code for lock_gate.ray using AI Agent. 20/08/2026. Code Start */
    setLocksReady(bypassesProgressLocks());
    /* Code end. ray using AI Agent. 20/08/2026 */
    refreshLocks();
    toast.success("Exited learner preview");
  }, [refreshLocks, bypassesProgressLocks]);

  const value = useMemo(
    () => ({
      progress,
      lockMap,
      /* Added the following code for lock_gate.ray using AI Agent. 20/08/2026. Code Start */
      locksReady,
      /* Code end. ray using AI Agent. 20/08/2026 */
      progressStatus,
      progressError,
      viewAs,
      learnerPreview,
      isAdminUser,
      showProgressRail,
      isPathLocked,
      refreshLocks,
      onResume,
      retryProgress,
      exitViewAs,
      exitLearnerPreview,
      setProgress,
      registerMenus,
      PROGRESS_SKIP_PREFIXES,
    }),
    [
      progress,
      lockMap,
      /* Added the following code for lock_gate.ray using AI Agent. 20/08/2026. Code Start */
      locksReady,
      /* Code end. ray using AI Agent. 20/08/2026 */
      progressStatus,
      progressError,
      viewAs,
      learnerPreview,
      isAdminUser,
      showProgressRail,
      isPathLocked,
      refreshLocks,
      onResume,
      retryProgress,
      exitViewAs,
      exitLearnerPreview,
      registerMenus,
    ]
  );

  return (
    <ProgressContext.Provider value={value}>{children}</ProgressContext.Provider>
  );
}

export function useProgress() {
  const ctx = useContext(ProgressContext);
  if (!ctx) {
    throw new Error("useProgress must be used within ProgressProvider");
  }
  return ctx;
}

/** Optional consumer when provider may be absent (e.g. tests). */
export function useProgressOptional() {
  return useContext(ProgressContext);
}
/* Code end. Ray using AI Agent. 27/07/2026 */
